import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EyeOff, Paperclip } from 'lucide-react';
import type { DeskAttachment, DeskAttachmentInput, TicketDetail } from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateTime, humanize } from '@/lib/utils';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, SkeletonText, Textarea, statusLabel } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { AttachmentPicker } from './AttachmentPicker';
import { formatBytes, openDeskAttachment, TICKET_STATUS_TONE, type DeskEndpoints } from './desk-api';

/**
 * One ticket and its conversation, shared by all three desks.
 *
 * The differences between them are passed in rather than branched on: whether
 * internal notes may be written, and what actions sit above the thread. The
 * server decides what is *readable* — a hospital never receives an internal
 * note in the first place, so nothing here has to remember to hide one.
 */
export function TicketThread({
  endpoints,
  ticketId,
  canWriteInternal = false,
  actions,
}: {
  endpoints: DeskEndpoints;
  ticketId: string;
  canWriteInternal?: boolean;
  actions?: (ticket: TicketDetail) => React.ReactNode;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const [files, setFiles] = useState<DeskAttachmentInput[]>([]);

  const ticket = useQuery({
    queryKey: ['ticket', endpoints.tickets, ticketId],
    queryFn: () => get<TicketDetail>(`${endpoints.tickets}/${ticketId}`),
  });

  const reply = useMutation({
    mutationFn: () =>
      post<TicketDetail>(`${endpoints.tickets}/${ticketId}/messages`, {
        body: body.trim(),
        attachments: files,
        isInternal: canWriteInternal && internal,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['ticket', endpoints.tickets, ticketId], updated);
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      setBody('');
      setFiles([]);
      setInternal(false);
    },
    onError: (error) => toast.error('Could not send the reply', describeError(error)),
  });

  if (ticket.isLoading) {
    return (
      <SkeletonText lines={3} />
    );
  }
  if (!ticket.data) return <Alert variant="destructive">That ticket could not be opened.</Alert>;

  const t = ticket.data;
  const settled = t.status === 'closed';

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
          <div className="min-w-0">
            <CardTitle className="text-base">{t.subject}</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              {t.number} · {t.tenantName} · raised by {t.raisedByName} · {dateTime(t.createdAt)}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Badge variant={TICKET_STATUS_TONE[t.status] ?? 'secondary'}>{statusLabel(t.status)}</Badge>
            <Badge variant="outline">{humanize(t.priority, 'priority')}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="whitespace-pre-wrap text-sm">{t.body}</p>
          <AttachmentRow endpoints={endpoints} attachments={t.attachments} />
          {t.escalationNote && (
            <Alert variant="warning">
              <span className="font-medium">Handed to the owner:</span> {t.escalationNote}
            </Alert>
          )}
          {t.resolutionNote && (
            <Alert variant="success">
              <span className="font-medium">Resolved:</span> {t.resolutionNote}
            </Alert>
          )}
          {actions && <div className="flex flex-wrap gap-2 pt-1">{actions(t)}</div>}
        </CardContent>
      </Card>

      <div className="space-y-3">
        {t.messages.map((message) => (
          <div
            key={message.id}
            className={`rounded-lg border p-3 ${
              message.isInternal ? 'border-dashed bg-muted/40' : 'bg-card'
            }`}
          >
            <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{message.authorName}</span>
              <span>{authorLabel(message.authorKind)}</span>
              <span>·</span>
              <span>{dateTime(message.createdAt)}</span>
              {message.isInternal && (
                <Badge variant="muted" className="gap-1">
                  <EyeOff className="h-3 w-3" /> internal
                </Badge>
              )}
            </div>
            <p className="whitespace-pre-wrap text-sm">{message.body}</p>
            <AttachmentRow endpoints={endpoints} attachments={message.attachments} />
          </div>
        ))}
        {t.messages.length === 0 && (
          <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            No replies yet.
          </p>
        )}
      </div>

      {settled ? (
        <Alert>This ticket is closed. Open a new one to carry on.</Alert>
      ) : (
        <Card>
          <CardContent className="space-y-3 pt-4">
            <Textarea
              rows={4}
              placeholder="Write a reply…"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <AttachmentPicker endpoints={endpoints} value={files} onChange={setFiles} />
            <div className="flex flex-wrap items-center justify-between gap-3">
              {canWriteInternal ? (
                <Checkbox
                  checked={internal}
                  onCheckedChange={setInternal}
                  label="Internal note — the hospital will not see this"
                />
              ) : (
                <span />
              )}
              <Button
                loading={reply.isPending}
                disabled={body.trim().length === 0}
                onClick={() => reply.mutate()}
              >
                Send reply
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function AttachmentRow({
  endpoints,
  attachments,
}: {
  endpoints: DeskEndpoints;
  attachments: DeskAttachment[];
}) {
  if (!attachments.length) return null;
  return (
    <ul className="flex flex-wrap gap-2">
      {attachments.map((file) => (
        <li key={file.id}>
          <button
            type="button"
            className="flex items-center gap-1.5 rounded-md border bg-muted/30 px-2 py-1 text-xs hover:bg-accent focus-ring"
            onClick={() => void openDeskAttachment(endpoints, file.id)}
          >
            <Paperclip className="h-3 w-3 text-muted-foreground" aria-hidden />
            <span className="max-w-[14rem] truncate">{file.fileName}</span>
            <span className="text-muted-foreground">{formatBytes(file.sizeBytes)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function authorLabel(kind: string): string {
  if (kind === 'tenant_user') return 'hospital';
  if (kind === 'contact_person') return 'account manager';
  return 'platform';
}
